import React, { useEffect, useState } from "react";
import {
  Radio,
  RadioGroup,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  FormHelperText,
  Tag,
  VStack,
  Input,
  FormErrorMessage,
  Button,
  Box,
  Heading,
  Icon,
  Text,
  useColorModeValue,
  Tooltip,
} from "@chakra-ui/react";
import styled from "@emotion/styled";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import useAxios from "axios-hooks";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { FaChargingStation, FaBolt, FaMoneyBillWave } from "react-icons/fa";
import { MdTimer } from "react-icons/md";

import { toastState } from "atoms/toastState";
import { activeConnectorState } from "atoms/activeConnectorState";
import logger from "infra/logger";
import { loggedInUserState } from "atoms/loggedInUserState";
import { accessLevels } from "utils/accessLevels";

// Glass morphism styled components
const GlassMorphismBox = styled(Box)`
  backdrop-filter: blur(16px);
  background: rgba(255, 255, 255, 0.7);
  border-radius: 20px;
  padding: 1rem;
  transition: all 0.3s ease;
  border: 1px solid rgba(35, 169, 121, 0.25);

  @media (min-width: 768px) {
    padding: 1.5rem;
  }

  &:hover {
    border-color: rgba(35, 169, 121, 0.4);
  }
`;

const GlassMorphismInput = styled(Input)`
  backdrop-filter: blur(6px);
  background: rgba(255, 255, 255, 0.7);
  transition: all 0.3s ease;
  border-radius: 12px;
  height: 40px;
  border: 1px solid rgba(35, 169, 121, 0.25);

  @media (min-width: 768px) {
    height: 45px;
  }

  &:hover,
  &:focus {
    border-color: #23A979;
    box-shadow: 0 4px 20px rgba(35, 169, 121, 0.25);
  }
`;

const StyledRadio = styled(Radio)`
  .chakra-radio__control {
    border: 2px solid rgba(35, 169, 121, 0.6);
    transition: all 0.2s ease;

    &[data-checked] {
      background: #23A979;
      border-color: #23A979;
      box-shadow: 0 0 0 3px rgba(35, 169, 121, 0.2);
    }

    &:hover {
      border-color: #23A979;
    }
  }

  .chakra-radio__label {
    font-weight: 500;
    transition: all 0.2s ease;
  }

  &:hover .chakra-radio__label {
    color: #23A979;
  }
`;

const ConnectorsData = [
  {
    name: "mode",
    type: "radio",
    label: "Select Mode",
    helperText: "Select Mode of your Charging",
    icon: FaBolt,
    options: [
      { name: "Money", Value: "Money", icon: FaMoneyBillWave },
      { name: "Units", Value: "Units", icon: FaBolt },
      // { name: "Time", Value: "Time", icon: MdTimer },
    ],
  },
  {
    name: "value",
    type: "input",
    label: "Value",
    helperText: "Enter Value in Rupees or Minutes or Units",
    icon: FaMoneyBillWave,
  },
  // {
  //   name: "mobileNumber",
  //   type: "input",
  //   label: "Enter Mobile Number",
  //   helperText: "please enter your mobile number",
  // },
];
const StartCharging = ({ refresh }) => {
  const activeConnector = useRecoilValue(activeConnectorState);
  const [mode, setMode] = useState("Money");
  const loggedInUser = useRecoilValue(loggedInUserState);
  const role = loggedInUser?.role;
  const canEdit = accessLevels[role]?.accessLevel <= 2;
  const setToast = useSetRecoilState(toastState);
  const [{ data, loading, error }, execute] = useAxios(
    { method: "POST", url: `/api/ocpp16/charging/start` },
    { manual: true }
  );

  const validationSchema = yup.object().shape({
    mode: yup.string().required("Mode is Required"),
    value: yup.number().moreThan(0).required(""),
    // mobileNumber: Validation_MobileNumber_Req,
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    register,
    setValue,
    getValues,
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(validationSchema),
  });

  // Theme colors
  const labelColor = useColorModeValue("gray.700", "gray.200");
  const iconColor = useColorModeValue("#23A979", "#ff9966");

  const controllers = ConnectorsData.map((ele, i) => {
    return (
      <Controller
        key={i}
        name={ele.name}
        control={control}
        render={({ field }) => (
          <FormControl
            as="fieldset"
            maxW={{
              base: "100%",
              md: "300px",
            }}
            isInvalid={errors[ele.name] ? true : false}
            mb={{ base: 3, md: 4 }}
          >
            <Flex align="center" mb={2}>
              <Icon as={ele.icon} color={iconColor} mr={2} boxSize={{ base: 4, md: 5 }} />
              <FormLabel
                as="legend"
                fontSize={{ base: "sm", md: "md" }}
                fontWeight={600}
                color={labelColor}
                m={0}
              >
                {ele.label}
              </FormLabel>
            </Flex>

            {ele.type === "radio" && (
              <RadioGroup
                {...field}
                defaultValue="Money"
                required
                isDisabled={!canEdit}
              >
                <HStack spacing={{ base: "12px", md: "24px" }} flexWrap="wrap">
                  {ele.options.map((option, idx) => (
                    <Tooltip
                      key={idx}
                      hasArrow
                      label={option.name}
                      placement="top"
                      bg="brand.500"
                      color="white"
                    >
                      <Box>
                        <StyledRadio
                          value={option.Value.toLowerCase()}
                          isDisabled={!canEdit}
                        >
                          <Flex align="center">
                            <Icon as={option.icon} mr={2} />
                            {option.name}
                          </Flex>
                        </StyledRadio>
                      </Box>
                    </Tooltip>
                  ))}
                </HStack>
              </RadioGroup>
            )}

            {ele.type === "input" && (
              <GlassMorphismInput
                required
                isDisabled={!canEdit}
                maxLength={10}
                placeholder="Enter value"
                {...field}
                type="number"
                min={0}
                _focus={{
                  borderColor: "brand.500",
                  boxShadow: "0 0 0 1px #23A979",
                }}
              />
            )}

            <FormHelperText fontSize={{ base: "2xs", md: "xs" }} color={labelColor} opacity={0.8}>
              {ele?.helperText}
            </FormHelperText>

            <FormErrorMessage mb={2} fontSize={{ base: "2xs", md: "xs" }}>
              {errors[ele.name] && errors[ele.name].message}
            </FormErrorMessage>
          </FormControl>
        )}
      />
    );
  });
  const startCharging = async () => {
    const formValues = getValues();
    let body = {};
    body["EvseSerialId"] = activeConnector.EvseSerialId;
    body["StationId"] = activeConnector.EvseId;
    body["IdTag"] = `${formValues.mobileNumber}`;
    body["Mode"] = formValues.mode;
    body["Value"] = parseFloat(formValues.value);
    try {
      const response = await execute({ data: body });
      setToast({
        isOpen: true,
        title: "Charging Started Successfully",
        description: response.data.data.Message,
        status: "success",
      });
    } catch (error) {
      const err = error?.response?.data?.error;
      logger.log(error);
      setToast({
        isOpen: true,
        title: "Error occured when starting Charger",
        description: err ? err : null,
        status: "error",
      });
    }
    refresh();
  };
  useEffect(() => {
    setValue("mode", "Money");
    setValue("value", 0);
  }, []);
  return (
    <GlassMorphismBox
      width="100%"
      position="relative"
      overflow="hidden"
      _before={{
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "3px",
        // background: "linear-gradient(90deg, #23A979 0%, #ff9966 100%)",
        zIndex: 1,
      }}
    >
      <Flex mb={{ base: 4, md: 6 }} align="center">
        <Icon as={FaChargingStation} color="brand.500" boxSize={{ base: 5, md: 6 }} mr={{ base: 2, md: 3 }} />
        <Heading size={{ base: "sm", md: "md" }} fontWeight={600} color={labelColor}>
          Configure Charging Session
        </Heading>
      </Flex>

      <Flex
        direction={{
          base: "column",
          md: "row",
        }}
        flexWrap={"wrap"}
        gap={6}
        alignItems={"flex-end"}
        justifyContent={"space-between"}
      >
        <Flex
          direction={{
            base: "column",
            md: "row",
          }}
          gap={6}
          flexWrap="wrap"
        >
          {controllers}
        </Flex>

        <Button
          isDisabled={
            activeConnector?.Status !== "Preparing" &&
            activeConnector?.Status !== "Available"
          }
          bg="brand.500"
          color="white"
          py={1}
          onClick={handleSubmit(startCharging)}
          borderRadius="md"
          leftIcon={<FaChargingStation size={20} />}
          isLoading={loading}
          _hover={{
            transform: "translateY(-2px)",
            boxShadow: "0 8px 16px rgba(35, 169, 121, 0.3)",
          }}
          _active={{
            transform: "translateY(0)",
            boxShadow: "0 4px 8px rgba(35, 169, 121, 0.3)",
          }}
          transition="all 0.3s ease"
          fontWeight={600}
          fontSize={{ base: "sm", md: "md" }}
          w={{ base: "100%", md: "auto" }}
          mt={{ base: 3, md: 0 }}
        >
          Start Charging
        </Button>
      </Flex>

      {activeConnector?.Status !== "Preparing" &&
        activeConnector?.Status !== "Available" && (
          <Text fontSize="sm" color="gray.500" mt={4} textAlign="right">
            Connector is not available for charging
          </Text>
        )}
    </GlassMorphismBox>
  );
};

export default StartCharging;
